import { useEffect, useState } from "react";
import type { BackgroundImageFraming, SceneState } from "../../../shared/api-contract";
import { useViewer } from "../../../state/ViewerProvider";
import { useDomainState, useEditorStore, useUi } from "../../../state/editor/useEditor";
import { Icon } from "../../kit";
import { CompactSlider, MiniBtn, Row, TextActionInput } from "../inspector/rows";
import { Select } from "../../kit/Select";
import { useCaps } from "../CapsProvider";
import { pickFile } from "../../../util/download";
import { LibraryGrid, type LibItem } from "../library/LibraryGrid";
import { useManifest } from "../library/useManifest";

interface GradientEntry { id: string; label: string; file: string; top: string; bottom: string }

/** Curated solid backgrounds — whites, neutrals, charcoal/black, plus a few tints. */
const SWATCHES: { id: string; label: string; hex: string }[] = [
  { id: "white", label: "White", hex: "#ffffff" },
  { id: "ivory", label: "Ivory", hex: "#f7f7f1" },
  { id: "gray50", label: "Gray 50", hex: "#f3f4f6" },
  { id: "gray200", label: "Gray 200", hex: "#e5e7eb" },
  { id: "gray300", label: "Gray 300", hex: "#d1d5db" },
  { id: "charcoal", label: "Charcoal", hex: "#333333" },
  { id: "black", label: "Black", hex: "#000000" },
  { id: "blush", label: "Blush", hex: "#f6e9e7" },
  { id: "sky", label: "Sky", hex: "#e7eef6" },
  { id: "sage", label: "Sage", hex: "#e8efe7" },
];

/**
 * Background panel — a grid of solid-color cards (each `setBackground(hex)`), a
 * "None" card (`setBackground(null)`), and a custom color input. Tracks the last
 * applied value locally for the active highlight. Gradients/transparent land in
 * Phase 2.
 *
 * The colour RPCs are a single round-trip and stay optimistic; the three image
 * paths (URL, upload, gradient tile) are network + texture decodes, so those
 * hold a pending state and only claim to be applied once they resolve.
 */
export function BackgroundPanel() {
  const { client } = useViewer();
  const r = client.ring;
  const [ui] = useUi();
  const store = useEditorStore();
  // Each of these buttons switches the ACTIVE background source in the viewer,
  // which turns the others off (an image replaces env-as-background, and so on).
  // Re-seed the scene slice so the inspector's env/blur rows next door stop
  // reporting a background that is no longer on screen.
  const resync = () => void store.refresh("scene");
  const [active, setActive] = useState<string | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // An uploaded image is inlined into the saved scene by the viewer (contract
  // v46), so it survives a reload like a URL-loaded one. An image background
  // with NO url is the exception — a picture over that size cap — and the user
  // has to hear about it here, before a Save quietly drops the backdrop.
  const scene = useDomainState("scene") as SceneState | undefined;
  const localOnlyImage = scene?.backgroundSource === "image" && !scene?.backgroundImageUrl;
  const { items: gradients, urlFor } = useManifest<GradientEntry>("/backgrounds/manifest.json", "background");

  // v49 backdrop framing. Hidden entirely on an older viewer: these are NEW
  // method names and RPC has no timeout, so calling them there would hang.
  const caps = useCaps();
  const imageLive = scene?.backgroundSource === "image";
  const canFrame = caps.backgroundFramingV49 && imageLive;
  const [framing, setFraming] = useState<BackgroundImageFraming | null>(null);
  useEffect(() => {
    if (!canFrame) { setFraming(null); return; }
    let alive = true;
    r.getBackgroundImageFraming()
      .then((f) => { if (alive) setFraming(f); })
      .catch(() => { /* leave the rows hidden rather than guessing a framing */ });
    return () => { alive = false; };
    // Re-read when the backdrop itself changes: a new picture keeps the framing,
    // and the rows must show what is actually in force rather than stale values.
  }, [canFrame, r, scene?.backgroundImageUrl]);
  const frame = (patch: Partial<BackgroundImageFraming>) =>
    void r.setBackgroundImageFraming(patch).then(setFraming).catch(fail);

  const errText = (e: unknown) => (e instanceof Error && e.message ? e.message : "Failed to apply background.");
  const fail = (e: unknown) => setError(errText(e));

  /** `sync` is off for the live colour-picker drag — it fires per pointer tick,
   *  and the picker's own blur re-seeds once at the end instead. */
  const apply = (hex: string | null, sync = true) => {
    setError(null);
    setActive(hex);
    // Never `void` the RPC: a rejected setBackground would otherwise be an
    // unhandled rejection and the card would keep claiming to be applied.
    r.setBackground(hex).catch(fail).finally(() => { if (sync) resync(); });
  };

  const uploadImage = async () => {
    // pickFile discards the rejection of its own callback, so capture the
    // failure inside and re-throw after the dialog round-trip.
    let failure: unknown;
    await pickFile("image/*", async (f) => {
      try {
        await r.setBackgroundImageFile(f);
        setActive(null);
      } catch (e) {
        failure = e;
      }
    });
    if (failure) throw failure;
  };

  /* The shape comes from the FILE, not the id.
   *
   * This used to read `g.id.startsWith("radial")`, which held only while ids came
   * from the static manifest as slugs. Once the library became API-served the id
   * is a Mongo ObjectId, the test was always false, and all six radial gradients
   * previewed as linear — silently, because the colours still came through and
   * applying one still loaded the right .svg. An id is an identity, not a
   * description. `file` carries the convention in both paths: "bg_radial_*.svg"
   * from the manifest, and the same name at the end of the API row's URL. */
  const isRadial = (g: GradientEntry) => /bg_radial/i.test(g.file ?? "");
  const gradItems: LibItem[] = gradients.map((g) => ({
    id: g.id,
    label: g.label,
    swatch: isRadial(g)
      ? `radial-gradient(circle at 50% 42%, ${g.top}, ${g.bottom})`
      : `linear-gradient(${g.top}, ${g.bottom})`,
  }));
  // Returned, not `void`ed, so the tile spins for the image fetch and the
  // check-mark lands only after the background actually changed.
  const applyGradient = (it: LibItem) => {
    const g = gradients.find((x) => x.id === it.id);
    if (!g) return;
    setError(null);
    return r.setBackgroundImageUrl(urlFor(g)).then(() => setActive(`grad:${g.id}`)).finally(resync);
  };

  return (
    <div className="flex flex-col gap-4 p-3">
      <div className="flex flex-col gap-2">
        <span className="text-[12px] font-semibold text-ed-text-2">Solid colour</span>
        <div className="grid grid-cols-4 gap-2">
          <button
            onClick={() => apply(null)}
            className={`flex aspect-square flex-col items-center justify-center gap-1 rounded-lg border text-[11px] text-ed-text-2 ${
              active === null ? "border-ed-accent ring-1 ring-ed-accent" : "border-ed-border hover:border-ed-accent"
            }`}
          >
            <Icon name="block" size={16} />
            None
          </button>
          {SWATCHES.map((s) => {
            const on = active?.toLowerCase() === s.hex.toLowerCase();
            return (
              <button
                key={s.id}
                title={s.label}
                onClick={() => apply(s.hex)}
                className={`relative aspect-square rounded-lg border transition-colors ${
                  on ? "border-ed-accent ring-1 ring-ed-accent" : "border-ed-border hover:border-ed-accent"
                }`}
                style={{ background: s.hex }}
              >
                {on ? (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <Icon name="check" size={16} className="text-ed-accent drop-shadow" />
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-[12px] font-semibold text-ed-text-2">Custom</span>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={active && active.startsWith("#") ? active : "#ffffff"}
            onChange={(e) => apply(e.target.value, false)}
            onBlur={resync}
            className="h-8 w-12 cursor-pointer rounded border border-ed-border bg-white p-0.5"
          />
          <span className="text-[12px] uppercase tabular-nums text-ed-text-3">{active ?? "none"}</span>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-[12px] font-semibold text-ed-text-2">Image</span>
        <TextActionInput
          placeholder="Image URL…"
          action="Load"
          // Returned so the field spins for the real fetch and keeps the URL if
          // it 404s, instead of clearing itself the instant Enter is pressed.
          onSubmit={(u) => {
            setError(null);
            return r.setBackgroundImageUrl(u).then(() => setActive(null)).finally(resync);
          }}
        />
        <div className="flex gap-1.5">
          <MiniBtn
            icon="upload"
            busy={uploadBusy}
            onClick={() => {
              setError(null);
              setUploadBusy(true);
              // pickFile settles when the image decode finishes, not when the
              // OS dialog opens, so the spinner covers the actual work.
              uploadImage().catch(fail).finally(() => { setUploadBusy(false); resync(); });
            }}
          >
            Upload image
          </MiniBtn>
          <MiniBtn icon="block" disabled={uploadBusy} onClick={() => { setError(null); setActive(null); r.setTransparentBackground().catch(fail).finally(resync); }}>
            Transparent
          </MiniBtn>
        </div>
        {error ? <span className="text-[11px] text-ed-red">{error}</span> : null}
        {localOnlyImage ? (
          <span className="text-[11px] text-ed-orange">
            This image is too large to save with the scene — it will be gone after a reload. Load it from a URL to keep it.
          </span>
        ) : null}
      </div>

      {canFrame && framing ? (
        <div className="flex flex-col gap-1">
          <div className="flex items-baseline justify-between">
            <span className="text-[12px] font-semibold text-ed-text-2">Framing</span>
            <MiniBtn
              icon="restart_alt"
              onClick={() => frame({ fit: "cover", scale: 1, offsetX: 0, offsetY: 0, rotation: 0 })}
            >
              Reset
            </MiniBtn>
          </div>
          <Row label="Fit">
            <Select
              value={framing.fit ?? "cover"}
              options={[
                { id: "cover", label: "Cover" },
                { id: "contain", label: "Contain" },
                { id: "stretch", label: "Stretch" },
              ]}
              onChange={(v) => frame({ fit: v as BackgroundImageFraming["fit"] })}
            />
          </Row>
          <Row label="Zoom">
            <CompactSlider
              value={framing.scale ?? 1}
              min={0.25} max={4} step={0.01}
              format={(v) => `${v.toFixed(2)}x`}
              onChange={(v) => frame({ scale: v })}
            />
          </Row>
          <Row label="Position X">
            <CompactSlider
              value={framing.offsetX ?? 0}
              min={-1} max={1} step={0.005}
              format={(v) => v.toFixed(3)}
              onChange={(v) => frame({ offsetX: v })}
            />
          </Row>
          <Row label="Position Y">
            <CompactSlider
              value={framing.offsetY ?? 0}
              min={-1} max={1} step={0.005}
              format={(v) => v.toFixed(3)}
              onChange={(v) => frame({ offsetY: v })}
            />
          </Row>
          <Row label="Rotation">
            {/* Contract is radians; the control is degrees, which is what a
                person means by "rotate the background". */}
            <CompactSlider
              value={((framing.rotation ?? 0) * 180) / Math.PI}
              min={-180} max={180} step={1}
              unit="deg"
              onChange={(v) => frame({ rotation: (v * Math.PI) / 180 })}
            />
          </Row>
          {(framing.fit ?? "cover") === "contain" ? (
            <span className="px-3 text-[11px] text-ed-text-3">
              Contain shows the whole picture; the area past its edge repeats the edge pixels rather than
              letterboxing.
            </span>
          ) : null}
        </div>
      ) : null}

      {gradItems.length ? (
        <div className="flex flex-col gap-2">
          <span className="text-[12px] font-semibold text-ed-text-2">Gradients</span>
          <LibraryGrid
            items={gradItems}
            activeId={active?.startsWith("grad:") ? active.slice(5) : null}
            onSelect={applyGradient}
            view={ui.libraryView}
            disabled={uploadBusy}
          />
        </div>
      ) : null}
    </div>
  );
}
